import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { registerChannel, unregisterChannel } from "@/lib/realtimeChannels";

/**
 * Live count of completed bulk-verify items waiting on the user's
 * approve/reject decision, plus any in-flight job progress. Drives the
 * "Bulk Review (12)" badge + "Verifying 8 of 20…" indicator on Profile.
 */
export function useBulkPending() {
  const { user } = useAuth();
  const [pending, setPending] = useState(0);
  const [active, setActive] = useState<{ completed: number; total: number } | null>(null);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const load = async () => {
      const [{ count }, { data: jobs }] = await Promise.all([
        supabase
          .from("bulk_verification_items")
          .select("id", { count: "exact", head: true })
          .eq("user_id", user.id)
          .eq("status", "complete")
          .eq("user_decision", "pending"),
        supabase
          .from("bulk_verification_jobs")
          .select("completed_items, total_items")
          .eq("user_id", user.id)
          .eq("status", "processing")
          .order("created_at", { ascending: false })
          .limit(1),
      ]);
      if (cancelled) return;
      setPending(count ?? 0);
      const j = jobs?.[0];
      setActive(j ? { completed: j.completed_items, total: j.total_items } : null);
    };
    void load();
    const key = `bulk-${user.id}`;
    registerChannel(key, () =>
      supabase
        .channel(key)
        .on("postgres_changes", { event: "*", schema: "public", table: "bulk_verification_items", filter: `user_id=eq.${user.id}` }, () => void load())
        .on("postgres_changes", { event: "*", schema: "public", table: "bulk_verification_jobs", filter: `user_id=eq.${user.id}` }, () => void load())
        .subscribe()
    );
    return () => { cancelled = true; unregisterChannel(key); };
  }, [user]);

  return { pending, active };
}